import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
} from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { useRouter } from "expo-router";
import { Colors } from "@/constants/Colors";
import { Button } from "@/components/Button";
import useUserStore from "@/store/user";
import useSubscriptionPayment from "@/hooks/sharedHook/useSubscriptionPayment";
import { IAP_ANNUAL_PLANS } from "@/constants/Configs";
import { FontFamilies } from "@/constants/Typography";
import LottieConfetti from "@/components/LottieConfetti";

export default function OnboardPaywallOfferScreen() {
  const { getUserProfile, userProfile } = useUserStore();
  const router = useRouter();
  const { requestSubscription, loading } = useSubscriptionPayment({
    sku: IAP_ANNUAL_PLANS.sku,
    userId: userProfile?.id,
    onSuccess: async () => {
      await getUserProfile();
      router.replace("/signup/onboard-success");
    },
  });

  const handleClaimOffer = () => {
    requestSubscription();
  };

  const handleCancelAnyway = () => {
    router.replace("/signup/onboard-paywall");
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" />
      <View style={styles.container}>
        {/* Content */}
        <View style={styles.content}>
          {/* Title Section */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>One-time offer</Text>
            <Text style={styles.subtitle}>This discount will only show up once.</Text>
          </View>

          {/* Big Discount Graphic */}
          <View style={styles.discountGraphic}>
            <Text style={styles.discountNumber}>85</Text>
            <View style={styles.percentSection}>
              <Text style={styles.percentSign}>%</Text>
              <View style={styles.offBadge}>
                <Text style={styles.offText}>OFF</Text>
              </View>
            </View>
          </View>

          {/* Description */}
          <View style={styles.descriptionContainer}>
            <Text style={styles.description}>
              Claim this offer and start filing claims today.
              Sparrow offers a full money-back guarantee, meaning you'll always
              come out ahead.
            </Text>
          </View>
        </View>

        {/* Bottom Buttons */}
        <View style={styles.bottomContainer}>
          <Button
            title={loading ? "Processing..." : "Claim one-time offer"}
            variant="primary"
            style={styles.claimButton}
            onPress={handleClaimOffer}
            disabled={loading}
          />
          <TouchableOpacity onPress={handleCancelAnyway} disabled={loading}>
            <Text style={styles.cancelText}>Cancel anyway</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Confetti */}
      <LottieConfetti active={true} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#fff",
  },
  container: {
    flex: 1,
    justifyContent: "space-between",
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 40,
    alignItems: "center",
  },
  titleSection: {
    alignItems: "center",
    marginBottom: 24,
    marginTop: 40,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 37,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#000",
    textAlign: "center",
    lineHeight: 23,
    marginBottom: 24,
  },
  discountGraphic: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: 40,
  },
  discountNumber: {
    fontSize: 140,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0678D5",
    lineHeight: 140,
    letterSpacing: -7,
  },
  percentSection: {
    marginTop: 10,
  },
  percentSign: {
    fontSize: 72,
    fontFamily: FontFamilies.Inter.Black,
    color: "#0678D5",
    lineHeight: 72,
    letterSpacing: -3.6,
    textAlign: "center",
  },
  offBadge: {
    backgroundColor: "#161D14",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  offText: {
    fontSize: 28,
    fontFamily: FontFamilies.Inter.Bold,
    color: "#fff",
    lineHeight: 32,
  },
  descriptionContainer: {
    paddingHorizontal: 16,
  },
  description: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
    lineHeight: 20,
  },
  bottomContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 12,
  },
  claimButton: {
    backgroundColor: Colors.primary,
  },
  cancelText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.primary,
    textAlign: "center",
    lineHeight: 23,
  },
});
